/**
 * Lista de notificaciones para el usuario actual.
 *
 * Pieza compartida con otro agente: este módulo es responsable de pintar
 * las filas y de gestionar el comportamiento por defecto (marcar leída +
 * navegar). El bloque específico de OPPORTUNITY_STALE añade un botón
 * "✨ Generar follow-up" que abre <FollowupModal /> con los datos de
 * notification.metadata sin disparar el click del item (stopPropagation).
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { Bell, Clock, Loader2, Sparkles } from 'lucide-react'
import { api } from '../../lib/api'
import FollowupModal from './FollowupModal'

export interface NotificationDto {
  id: string
  type: string
  title: string
  body: string | null
  link: string | null
  metadata: Record<string, unknown> | null
  readAt: string | null
  createdAt: string
}

interface NotificationsListResponse {
  items: NotificationDto[]
}

interface OpportunityStaleMetadata {
  opportunityId: string
  opportunityCode: string
  opportunityTitle: string
  daysSinceLastEvent: number
}

function getStaleMetadata(n: NotificationDto): OpportunityStaleMetadata | null {
  if (n.type !== 'OPPORTUNITY_STALE') return null
  const m = n.metadata
  if (!m || typeof m !== 'object') return null
  const raw = m as Record<string, unknown>
  const opportunityId = typeof raw.opportunityId === 'string' ? raw.opportunityId : null
  const opportunityCode =
    typeof raw.opportunityCode === 'string' ? raw.opportunityCode : null
  const opportunityTitle =
    typeof raw.opportunityTitle === 'string' ? raw.opportunityTitle : null
  const daysSinceLastEvent =
    typeof raw.daysSinceLastEvent === 'number' ? raw.daysSinceLastEvent : null
  if (!opportunityId) return null
  return {
    opportunityId,
    opportunityCode: opportunityCode ?? '',
    opportunityTitle: opportunityTitle ?? i18n.t('notifications:opportunityWithoutTitle'),
    daysSinceLastEvent: daysSinceLastEvent ?? 0,
  }
}

function relativeTime(iso: string): string {
  const date = new Date(iso)
  const diffMs = Date.now() - date.getTime()
  const minutes = Math.round(diffMs / 60000)
  if (minutes < 1) return i18n.t('notifications:relativeTime.justNow')
  if (minutes < 60) return i18n.t('notifications:relativeTime.minutes', { count: minutes })
  const hours = Math.round(minutes / 60)
  if (hours < 24) return i18n.t('notifications:relativeTime.hours', { count: hours })
  const days = Math.round(hours / 24)
  return i18n.t('notifications:relativeTime.days', { count: days })
}

export default function NotificationsList() {
  const { t } = useTranslation('notifications')
  const navigate = useNavigate()
  const [items, setItems] = useState<NotificationDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Modal de follow-up: guardamos el opportunityId/title abiertos.
  const [followup, setFollowup] = useState<{
    opportunityId: string
    opportunityTitle: string
  } | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<NotificationsListResponse>('/notifications')
        if (!cancelled) setItems(res.items)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('errors.loadFailed'))
          setItems([])
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const sorted = useMemo(() => {
    if (!items) return []
    return [...items].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }, [items])

  const markRead = async (n: NotificationDto) => {
    if (n.readAt) return
    try {
      await api.post(`/notifications/${n.id}/read`)
      setItems((prev) =>
        prev?.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)) ??
        prev
      )
    } catch {
      /* silent: el otro agente expone el endpoint; si falla, no rompemos el flujo */
    }
  }

  const onItemClick = async (n: NotificationDto) => {
    await markRead(n)
    if (n.link) navigate(n.link)
  }

  if (items === null) {
    return (
      <div className="surface p-4">
        <p className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('loading')}
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="surface p-4">
        <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>
      </div>
    )
  }

  if (sorted.length === 0) {
    return (
      <div className="surface p-4">
        <p className="t-muted text-sm flex items-center gap-2">
          <Bell size={14} /> {t('empty')}
        </p>
      </div>
    )
  }

  return (
    <div className="surface p-2">
      <ul className="divide-y divide-base/40">
        {sorted.map((n) => {
          const stale = getStaleMetadata(n)
          const unread = !n.readAt
          return (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => void onItemClick(n)}
                className={
                  'w-full text-left px-3 py-2.5 rounded-lg hover:bg-hover transition flex gap-3 ' +
                  (unread ? 't-primary' : 't-secondary')
                }
              >
                <div className="pt-0.5">
                  <Bell
                    size={14}
                    className={unread ? 'text-toga-700 dark:text-toga-300' : 't-faint'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    {stale && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFollowup({
                            opportunityId: stale.opportunityId,
                            opportunityTitle: stale.opportunityCode
                              ? `${stale.opportunityCode} — ${stale.opportunityTitle}`
                              : stale.opportunityTitle,
                          })
                        }}
                        className="shrink-0 px-2 py-0.5 rounded-md text-[11px] font-semibold border border-toga-500/40 bg-toga-500/10 text-toga-800 dark:text-toga-100 hover:bg-toga-500/20 flex items-center gap-1"
                        title={t('followup.tooltip')}
                      >
                        <Sparkles size={11} /> {t('followup.button')}
                      </button>
                    )}
                  </div>
                  {n.body && (
                    <p className="text-xs t-muted mt-0.5 line-clamp-2">{n.body}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-[11px] t-faint">
                    <Clock size={10} />
                    <span>{relativeTime(n.createdAt)}</span>
                    {n.link && (
                      <Link
                        to={n.link}
                        onClick={(e) => e.stopPropagation()}
                        className="ml-1 hover:underline"
                      >
                        {t('open')}
                      </Link>
                    )}
                  </div>
                </div>
              </button>
            </li>
          )
        })}
      </ul>

      {followup && (
        <FollowupModal
          open={true}
          onClose={() => setFollowup(null)}
          opportunityId={followup.opportunityId}
          opportunityTitle={followup.opportunityTitle}
        />
      )}
    </div>
  )
}
